<template>
    <UploadModalWrapper>
        <ModalOverlay @click="$emit('close')">
            <ModalContent @click.stop>
                <ModalHeader>
                    <ModalTitle>{{ selectedBeneficiary.name }}님 문서 등록</ModalTitle>
                    <CloseButton @click="$emit('close')">&times;</CloseButton>
                </ModalHeader>

                <ModalBody>
                    <UploadArea :class="{ 'drag-over': isDragOver }" @dragenter="handleDragEnter"
                        @dragover="handleDragOver" @dragleave="handleDragLeave" @drop="handleDrop"
                        @click="triggerFileInput">
                        <input ref="fileInput" type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                            @change="handleFileSelect" style="display: none;">

                        <UploadIcon v-if="!selectedFiles.length">📁</UploadIcon>
                        <UploadText v-if="!selectedFiles.length">
                            파일을 드래그하거나 클릭하여 선택하세요
                        </UploadText>
                        <UploadSubText v-if="!selectedFiles.length">
                            이미지, PDF, 문서 파일 등을 등록할 수 있습니다<br>
                            (최대 10MB, 여러 파일 선택 가능)
                        </UploadSubText>

                        <!-- 선택된 파일 목록 -->
                        <SelectedFilesList v-if="selectedFiles.length">
                            <FileItem v-for="(file, index) in selectedFiles" :key="index">
                                <FileInfo>
                                    <FileName>{{ file.name }}</FileName>
                                    <FileSize>{{ formatFileSize(file.size) }}</FileSize>
                                </FileInfo>
                                <RemoveFileButton @click.stop="removeFile(index)">
                                    &times;
                                </RemoveFileButton>
                            </FileItem>
                        </SelectedFilesList>
                    </UploadArea>

                    <!-- 등록 진행률 -->
                    <UploadProgress v-if="isUploading">
                        <ProgressBar>
                            <ProgressFill :style="{ width: uploadProgress + '%' }"></ProgressFill>
                        </ProgressBar>
                        <ProgressText>{{ uploadProgress }}% 등록 중...</ProgressText>
                    </UploadProgress>
                </ModalBody>

                <ModalFooter>
                    <CancelButton @click="$emit('close')">취소</CancelButton>
                    <UploadButton @click="handleUpload" :disabled="!selectedFiles.length || isUploading">
                        {{ isUploading ? '등록 중...' : '등록' }}
                    </UploadButton>
                </ModalFooter>
            </ModalContent>
        </ModalOverlay>
    </UploadModalWrapper>
</template>

<script>
import * as UploadDocumentModalCss from '@/assets/styles/manage/modal/UploadDocumentModalCss.js';

export default {
    name: 'UploadDocumentModal',

    components: {
        ...UploadDocumentModalCss
    },

    props: {
        selectedBeneficiary: {
            type: Object,
            default: null
        }
    },

    emits: ['close', 'upload-success'],

    data() {
        return {
            selectedFiles: [],
            isDragOver: false,
            isUploading: false,
            uploadProgress: 0,
            escapeHandler: null
        }
    },

    methods: {
        triggerFileInput() {
            this.$refs.fileInput.click();
        },

        handleFileSelect(event) {
            const files = Array.from(event.target.files);
            this.addFiles(files);
        },

        handleDragEnter(e) {
            e.preventDefault();
            this.isDragOver = true;
        },

        handleDragOver(e) {
            e.preventDefault();
        },

        handleDragLeave(e) {
            e.preventDefault();
            this.isDragOver = false;
        },

        handleDrop(e) {
            e.preventDefault();
            this.isDragOver = false;
            const files = Array.from(e.dataTransfer.files);
            this.addFiles(files);
        },

        addFiles(files) {
            const validFiles = files.filter(file => {
                // 파일 크기 검증 (10MB)
                if (file.size > 10 * 1024 * 1024) {
                    this.$swal.fire({
                        title: '파일 크기 초과',
                        text: `${file.name}은(는) 10MB를 초과합니다.`,
                        icon: 'warning'
                    });
                    return false;
                }

                // 파일 형식 검증
                const allowedTypes = [
                    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
                    'application/pdf',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'application/vnd.ms-excel',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                ];

                if (!allowedTypes.includes(file.type)) {
                    this.$swal.fire({
                        title: '지원하지 않는 파일 형식',
                        text: `${file.name}은(는) 지원하지 않는 파일 형식입니다.`,
                        icon: 'warning'
                    });
                    return false;
                }

                return true;
            });

            // 중복 파일 체크
            const newFiles = validFiles.filter(newFile => {
                return !this.selectedFiles.some(existingFile =>
                    existingFile.name === newFile.name && existingFile.size === newFile.size
                );
            });

            this.selectedFiles.push(...newFiles);
        },

        removeFile(index) {
            this.selectedFiles.splice(index, 1);
        },

        async handleUpload() {
            if (!this.selectedFiles.length) return;

            if (!this.selectedBeneficiary || !this.selectedBeneficiary.name) {
                this.$swal.fire({
                    title: '오류',
                    text: '수급자가 선택되지 않았습니다.',
                    icon: 'error'
                });
                return;
            }

            this.isUploading = true;
            this.uploadProgress = 0;

            try {
                const uploadPromises = this.selectedFiles.map(async (file, index) => {
                    const formData = new FormData();
                    formData.append('file', file);

                    // 수급자명 추가 (필수)
                    formData.append('beneficiaryName', this.selectedBeneficiary.name);

                    // 메타데이터 추가
                    const metadata = {
                        originalName: file.name,
                        uploadDate: new Date().toISOString(),
                        fileType: file.type,
                        fileSize: file.size,
                        beneficiaryName: this.selectedBeneficiary.name
                    };
                    formData.append('metadata', JSON.stringify(metadata));

                    console.log('파일 업로드 시작:', {
                        fileName: file.name,
                        beneficiaryName: this.selectedBeneficiary.name
                    });

                    const response = await this.$axios.post(
                        `${this.$apiUrls.hanqmedi}/documents/upload`,
                        formData,
                        {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            },
                            onUploadProgress: (progressEvent) => {
                                const progress = Math.round(
                                    ((index + progressEvent.loaded / progressEvent.total) / this.selectedFiles.length) * 100
                                );
                                this.uploadProgress = progress;
                            }
                        }
                    );

                    if (!response.data.success) {
                        throw new Error(`${file.name}: ${response.data.message}`);
                    }

                    return response.data;
                });

                await Promise.all(uploadPromises);

                this.$swal.fire({
                    title: '성공',
                    text: `${this.selectedBeneficiary.name}님의 문서 ${this.selectedFiles.length}개가 등록되었습니다.`,
                    icon: 'success'
                });

                // 부모 컴포넌트에 등록 성공 알림
                this.$emit('upload-success');
                this.resetModal();

            } catch (error) {
                console.error('파일 등록 실패:', error);
                this.$swal.fire({
                    title: '등록 실패',
                    text: error.message || '파일 등록 중 오류가 발생했습니다.',
                    icon: 'error'
                });
            } finally {
                this.isUploading = false;
                this.uploadProgress = 0;
            }
        },

        resetModal() {
            this.selectedFiles = [];
            this.isDragOver = false;
            this.uploadProgress = 0;
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        handleEscape(e) {
            if (e.key === 'Escape') {
                this.$emit('close');
            }
        }
    },

    mounted() {
        // ESC 키로 모달 닫기
        this.escapeHandler = this.handleEscape;
        document.addEventListener('keydown', this.escapeHandler);
    },

    unmounted() {
        // 컴포넌트 언마운트 시 이벤트 리스너 제거
        if (this.escapeHandler) {
            document.removeEventListener('keydown', this.escapeHandler);
        }
    }
}
</script>